import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Stack,
  Checkbox,
  CheckboxGroup,
  Box,
  HStack,
  Input,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
  Flex,
} from '@chakra-ui/react';
import { useState, useMemo } from 'react';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanColor } from '../helivan/HelivanColor';
import { DemographicFilterSelect } from './DemographicFilterSelect';
import {
  Subtopic,
  PerspectiveBaseInfoFragment,
  DtType,
  ProjectQuestion,
  useGetPerspectiveByDtTopicProjectLazyQuery,
} from '../../generated/graphql';
import * as XLSX from 'xlsx';
import { Document, Paragraph, TextRun, Packer } from 'docx';
import CreateChatLoading from './CreateChatLoading';
import { HelivanSelector } from '../helivan/HelivanSelector';

// Helper to get entity info from perspective (either digitalTwin or customAgent)
function getEntityInfo(perspective: PerspectiveBaseInfoFragment): {
  id: string;
  name: string;
  digitalTwin?: PerspectiveBaseInfoFragment['digitalTwin'];
} {
  if (perspective.digitalTwin) {
    return { id: perspective.digitalTwin.id, name: perspective.digitalTwin.name, digitalTwin: perspective.digitalTwin };
  }
  if (perspective.customAgent) {
    return { id: perspective.customAgent.id, name: perspective.customAgent.name };
  }
  return { id: 'unknown', name: 'Unknown' };
}

interface DownloadReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  subtopics: Subtopic[];
  perspectives: PerspectiveBaseInfoFragment[];
  projectTopic: string;
  dtType: DtType;
  questions: ProjectQuestion[];
  projectId: string;
}

export const DownloadReportModal = ({
  isOpen,
  onClose,
  subtopics,
  perspectives,
  projectTopic,
  dtType,
  questions,
  projectId,
}: DownloadReportModalProps) => {
  const [reportType, setReportType] = useState<'dataset' | 'faq'>('dataset');
  const [selectedSubtopic, setSelectedSubtopic] = useState<string>(
    subtopics.length > 0 ? subtopics[0].name : ''
  );
  const [selectedDemographics, setSelectedDemographics] = useState<
    Set<{ key: string; value: string }> | undefined
  >();
  const [selectedTwinIds, setSelectedTwinIds] = useState<Set<string>>(
    new Set()
  );
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('');
  const [currentScreen, setCurrentScreen] = useState<1 | 2>(1);
  const [getPerspectiveByDt] = useGetPerspectiveByDtTopicProjectLazyQuery();

  // Helper function to check if a digital twin has selected demographics
  const hasDemographics = (dt: any) => {
    if (!selectedDemographics || selectedDemographics.size === 0) {
      return true;
    }

    const demographicsArray = Array.from(selectedDemographics);

    // Group demographics by key (category)
    const demographicsByKey = new Map<string, string[]>();
    demographicsArray.forEach((demo) => {
      if (!demographicsByKey.has(demo.key)) {
        demographicsByKey.set(demo.key, []);
      }
      demographicsByKey.get(demo.key)!.push(demo.value);
    });

    // Check congress data
    if (dt.congressData) {
      // For each category, check if the twin matches at least one value (OR within category)
      for (const [key, values] of Array.from(demographicsByKey)) {
        let matchesCategory = false;

        if (key === 'party') {
          matchesCategory = values.includes(dt.congressData.party);
        } else if (key === 'state') {
          matchesCategory = values.includes(dt.congressData.state);
        } else if (key === 'chamber') {
          matchesCategory = values.includes(dt.congressData.chamber);
        }

        // If it doesn't match ANY value in this category, return false (AND between categories)
        if (!matchesCategory) {
          return false;
        }
      }
      return true; // Matches all categories
    }

    // Check early stage VC data
    if (dt.earlyStageVcData) {
      for (const [key, values] of Array.from(demographicsByKey)) {
        let matchesCategory = false;

        if (key === 'location') {
          matchesCategory = values.includes(dt.earlyStageVcData.location);
        }

        if (!matchesCategory) {
          return false;
        }
      }
      return true;
    }

    // Check fortune CEO data
    if (dt.fortuneCeoData) {
      for (const [key, values] of Array.from(demographicsByKey)) {
        let matchesCategory = false;

        if (key === 'industry') {
          matchesCategory = values.includes(dt.fortuneCeoData.industry);
        }

        if (!matchesCategory) {
          return false;
        }
      }
      return true;
    }

    // Check movies data
    if (dt.moviesMediumFullData || dt.moviesSimpleData) {
      const movieData = dt.moviesMediumFullData || dt.moviesSimpleData;

      for (const [key, values] of Array.from(demographicsByKey)) {
        let matchesCategory = false;

        if (key === 'gender') {
          matchesCategory = values.includes(movieData.gender);
        } else if (key === 'age') {
          matchesCategory = values.includes(movieData.age);
        } else if (dt.moviesMediumFullData) {
          if (key === 'race') {
            matchesCategory = values.includes(dt.moviesMediumFullData.race);
          } else if (key === 'region') {
            matchesCategory = values.includes(dt.moviesMediumFullData.region);
          }
        }

        if (!matchesCategory) {
          return false;
        }
      }
      return true;
    }

    return false;
  };

  // Filter perspectives based on selected subtopic and demographics
  const filteredPerspectives = useMemo(() => {
    let filtered = perspectives;

    // Filter by subtopic
    filtered = filtered.filter((p) => p.topic === selectedSubtopic);

    // Filter by demographics (only applies to digital twins)
    if (selectedDemographics && selectedDemographics.size > 0) {
      filtered = filtered.filter((p) => p.digitalTwin && hasDemographics(p.digitalTwin));
    }

    return filtered;
  }, [perspectives, selectedSubtopic, selectedDemographics]);

  const handleDownloadDataset = () => {
    // Remove duplicates by entity ID
    const uniquePerspectives = Array.from(
      new Map(filteredPerspectives.map((p) => [getEntityInfo(p).id, p])).values()
    );

    // Sort perspectives by X value (highest to lowest)
    const sortedPerspectives = [...uniquePerspectives].sort(
      (a, b) => b.x - a.x
    );

    // Prepare data for Excel
    const data = sortedPerspectives.map((perspective) => {
      const entity = getEntityInfo(perspective);
      const dt = entity.digitalTwin;
      const rowData: any = {
        'Persona Name': entity.name,
        'Score (X axis value)': perspective.x.toFixed(3),
      };

      // Add metadata based on digital twin type (custom agents don't have this data)
      if (dt?.congressData) {
        rowData['Twitter Handle'] = dt.congressData.twitterHandle || '';
        rowData['LinkedIn Handle'] = dt.congressData.linkedinHandle || '';
        rowData['Party'] = dt.congressData.party || '';
        rowData['State'] = dt.congressData.state || '';
        rowData['Chamber'] = dt.congressData.chamber || '';
      } else if (dt?.earlyStageVcData) {
        rowData['Twitter Handle'] = dt.earlyStageVcData.twitterHandle || '';
        rowData['LinkedIn Handle'] = dt.earlyStageVcData.linkedinHandle || '';
        rowData['Firm'] = dt.earlyStageVcData.firm || '';
        rowData['Title'] = dt.earlyStageVcData.title || '';
        rowData['Location'] = dt.earlyStageVcData.location || '';
      } else if (dt?.fortuneCeoData) {
        rowData['Twitter Handle'] = dt.fortuneCeoData.twitterHandle || '';
        rowData['LinkedIn Handle'] = dt.fortuneCeoData.linkedinHandle || '';
        rowData['Company'] = dt.fortuneCeoData.company || '';
        rowData['Industry'] = dt.fortuneCeoData.industry || '';
      } else if (dt?.moviesMediumFullData) {
        rowData['Name'] = dt.moviesMediumFullData.name || '';
        rowData['Gender'] = dt.moviesMediumFullData.gender || '';
        rowData['Age'] = dt.moviesMediumFullData.age || '';
        rowData['Race'] = dt.moviesMediumFullData.race || '';
        rowData['Region'] = dt.moviesMediumFullData.region || '';
        rowData['Education'] = dt.moviesMediumFullData.education || '';
        rowData['Employment'] = dt.moviesMediumFullData.employment || '';
        rowData['Income'] = dt.moviesMediumFullData.income || '';
      } else if (dt?.moviesSimpleData) {
        rowData['Name'] = dt.moviesSimpleData.name || '';
        rowData['Gender'] = dt.moviesSimpleData.gender || '';
        rowData['Age'] = dt.moviesSimpleData.age || '';
      }

      return rowData;
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dataset Overview');

    const filename = `${projectTopic}_${selectedSubtopic}_dataset.xlsx`;

    // Download the file
    XLSX.writeFile(wb, filename);

    handleClose();
  };

  const handleDownloadFAQ = async () => {
    if (selectedTwinIds.size === 0 || selectedTwinIds.size > 10) {
      alert('Please select between 1 and 10 Personas');
      return;
    }

    setIsLoading(true);
    setLoadingMessage('Fetching Personas...');

    try {
      // Fetch opinions for selected twins
      const faqData = await Promise.all(
        Array.from(selectedTwinIds).map(async (twinId) => {
          const result = await getPerspectiveByDt({
            variables: {
              dtId: twinId,
              topic: selectedSubtopic,
              projectId: projectId,
            },
          });

          const perspective = result.data?.getPerspectiveByDtTopicProject;
          const entityName = perspective?.digitalTwin?.name ?? perspective?.customAgent?.name ?? 'Unknown';
          return {
            twinName: entityName,
            opinions: perspective?.opinions || [],
          };
        })
      );

      // Update loading message
      setLoadingMessage('Finding relevant FAQs...');

      // Create a map of all questions and their answers by persona
      const questionMap = new Map<
        string,
        { question: string; answers: { twinName: string; answer: string }[] }
      >();

      // Collect all unique questions and their answers
      faqData.forEach((twinData) => {
        twinData.opinions.forEach((opinion: any) => {
          if (!questionMap.has(opinion.question)) {
            questionMap.set(opinion.question, {
              question: opinion.question,
              answers: [],
            });
          }
          questionMap.get(opinion.question)!.answers.push({
            twinName: twinData.twinName,
            answer: opinion.answer,
          });
        });
      });

      // Update loading message
      setLoadingMessage('Compiling FAQs...');

      // Create Word document organized by question
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'FAQ Report',
                    bold: true,
                    size: 32, // 16pt for title
                    font: 'Arial',
                  }),
                ],
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'Topic: ',
                    bold: true,
                    size: 24,
                    font: 'Arial',
                  }),
                  new TextRun({ text: projectTopic, size: 24, font: 'Arial' }),
                ],
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'Subtopic: ',
                    bold: true,
                    size: 24,
                    font: 'Arial',
                  }),
                  new TextRun({
                    text: selectedSubtopic,
                    size: 24,
                    font: 'Arial',
                  }),
                ],
              }),
              new Paragraph({ text: '' }), // Empty line
              ...Array.from(questionMap.values()).flatMap(
                (questionData, index) => {
                  const paragraphs: Paragraph[] = [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: questionData.question,
                          bold: true,
                          size: 24, // 12pt in half-points
                          font: 'Arial',
                        }),
                      ],
                    }),
                  ];

                  // Add each persona's answer
                  questionData.answers.forEach((answer) => {
                    paragraphs.push(
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: answer.twinName,
                            underline: {
                              type: 'single',
                              color: '#000000',
                            },
                            size: 24, // 12pt in half-points
                            font: 'Arial',
                          }),
                        ],
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: answer.answer,
                            size: 24, // 12pt in half-points
                            font: 'Arial',
                          }),
                        ],
                      }),
                      new Paragraph({ text: '' }) // Empty line after each answer
                    );
                  });

                  // Add empty line between questions (but not after the last one)
                  if (index < questionMap.size - 1) {
                    paragraphs.push(new Paragraph({ text: '' }));
                  }

                  return paragraphs;
                }
              ),
            ],
          },
        ],
      });

      // Generate and download the document
      const blob = await Packer.toBlob(doc);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projectTopic}_${selectedSubtopic}_FAQ.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      handleClose();
    } catch (error) {
      console.error('Error generating FAQ:', error);
      alert('Error generating FAQ report');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    if (reportType === 'dataset') {
      handleDownloadDataset();
    } else {
      handleDownloadFAQ();
    }
  };

  const handleNext = () => {
    setCurrentScreen(2);
  };

  const handleBack = () => {
    setCurrentScreen(1);
  };

  const handleClose = () => {
    setCurrentScreen(1);
    setReportType('dataset');
    setSelectedSubtopic('');
    setSelectedDemographics(undefined);
    setSelectedTwinIds(new Set());
    setSearchTerm('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody minHeight="400px" maxHeight="600px" overflowY="scroll">
          <HelivanText
            pb={6}
            pt={10}
            textAlign="center"
            variant="pageHeader"
            colorOverride="#114471"
          >
            Download Report
          </HelivanText>
          {isLoading ? (
            <Box
              py={8}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <CreateChatLoading
                isLoading={isLoading}
                loadingText={loadingMessage}
                size="md"
              />
            </Box>
          ) : (
            <Stack spacing={6}>
              {currentScreen === 1 ? (
                // Screen 1: Report Type Selection
                <>
                  <HelivanSelector
                    question="Report Type"
                    options={[
                      { value: 'Dataset Overview', key: 'dataset' },
                      { value: 'FAQ Report', key: 'faq' },
                    ]}
                    selectedOption={reportType}
                    onSelect={(value) => setReportType(value)}
                  />

                  <Box>
                    <HelivanText variant="largeBody" colorOverride="black">
                      Report will include
                    </HelivanText>
                    <Stack spacing={0} mt={1}>
                      <HelivanText
                        variant="largeBody"
                        colorOverride={HelivanColor.text.gray}
                      >
                        {reportType === 'dataset'
                          ? 'Persona Name, score, and relevant metadata'
                          : 'Questions organized with all persona responses together'}
                      </HelivanText>
                    </Stack>
                  </Box>
                </>
              ) : (
                // Screen 2: Topic Selection and Filters
                <>
                  <HelivanSelector
                    options={subtopics.map((subtopic) => ({
                      key: subtopic.name,
                      value: subtopic.name,
                    }))}
                    question="Topic of Report"
                    selectedOption={selectedSubtopic}
                    onSelect={(value) => {
                      setSelectedSubtopic(value);
                    }}
                  />

                  {selectedSubtopic && (
                    <>
                      {reportType === 'dataset' ? (
                        <Box>
                          <HelivanText
                            variant="largeBody+"
                            colorOverride="black"
                            marginBottom={2}
                          >
                            Filter by Demographics (Optional)
                          </HelivanText>
                          <DemographicFilterSelect
                            dtType={dtType}
                            selectedDemographics={selectedDemographics}
                            setSelectedDemographics={setSelectedDemographics}
                          />
                        </Box>
                      ) : (
                        <Box>
                          <HelivanText
                            variant="largeBody+"
                            colorOverride="black"
                            marginBottom={2}
                          >
                            Select Agents (up to 10)
                          </HelivanText>

                          <Box>
                            <Input
                              placeholder="Search..."
                              mb={3}
                              onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <Box
                              maxHeight="200px"
                              overflowY="auto"
                              border="1px solid"
                              borderColor={HelivanColor.border.primary}
                              borderRadius="4px"
                              p={2}
                            >
                              <CheckboxGroup
                                value={Array.from(selectedTwinIds)}
                                onChange={(values: string[]) => {
                                  if (values.length <= 10) {
                                    setSelectedTwinIds(new Set(values));
                                  }
                                }}
                              >
                                <Stack spacing={2}>
                                  {(() => {
                                    const twinMap = new Map();
                                    perspectives.forEach((p) => {
                                      const entity = getEntityInfo(p);
                                      twinMap.set(entity.id, {
                                        id: entity.id,
                                        name: entity.name,
                                      });
                                    });
                                    const uniqueTwins = Array.from(
                                      twinMap.values()
                                    );
                                    const filtered = searchTerm
                                      ? uniqueTwins.filter((twin) =>
                                          twin.name
                                            .toLowerCase()
                                            .includes(searchTerm.toLowerCase())
                                        )
                                      : uniqueTwins;

                                    return filtered.map((twin) => (
                                      <Checkbox
                                        key={twin.id}
                                        value={twin.id}
                                        isDisabled={
                                          !selectedTwinIds.has(twin.id) &&
                                          selectedTwinIds.size >= 10
                                        }
                                      >
                                        <HelivanText variant="smallBody">
                                          {twin.name}
                                        </HelivanText>
                                      </Checkbox>
                                    ));
                                  })()}
                                </Stack>
                              </CheckboxGroup>
                            </Box>
                            {selectedTwinIds.size > 0 && (
                              <Wrap mt={3}>
                                {Array.from(selectedTwinIds).map((twinId) => {
                                  const perspective = perspectives.find(
                                    (p) => getEntityInfo(p).id === twinId
                                  );
                                  const twinName = perspective ? getEntityInfo(perspective).name : 'Unknown';
                                  return (
                                    <WrapItem key={twinId}>
                                      <Tag
                                        size="sm"
                                        borderRadius="full"
                                        variant="solid"
                                        colorScheme="blue"
                                      >
                                        <TagLabel>
                                          {twinName}
                                        </TagLabel>
                                        <TagCloseButton
                                          onClick={() => {
                                            const newSet = new Set(
                                              selectedTwinIds
                                            );
                                            newSet.delete(twinId);
                                            setSelectedTwinIds(newSet);
                                          }}
                                        />
                                      </Tag>
                                    </WrapItem>
                                  );
                                })}
                              </Wrap>
                            )}
                          </Box>
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}
            </Stack>
          )}
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3} width="full">
            {currentScreen === 1 ? (
              <Flex
                width="full"
                alignItems="center"
                justifyContent="space-between"
              >
                <HStack spacing="6px">
                  <Box
                    borderRadius="8px"
                    height="12px"
                    width="20px"
                    bg="#114471"
                  />
                  <Box
                    borderRadius="8px"
                    height="12px"
                    width="48px"
                    bg="#ADCCDD"
                  />
                </HStack>
                <HStack spacing={2}>
                  <HelivanButton variant="ghost" onClick={handleClose}>
                    Cancel
                  </HelivanButton>
                  <HelivanButton
                    variant="action"
                    onClick={handleNext}
                    isDisabled={!reportType}
                  >
                    Next
                  </HelivanButton>
                </HStack>
              </Flex>
            ) : (
              <Flex
                width="full"
                alignItems="center"
                justifyContent="space-between"
              >
                <HStack spacing="6px">
                  <Box
                    borderRadius="8px"
                    height="12px"
                    width="48px"
                    bg="#114471"
                  />
                  <Box
                    borderRadius="8px"
                    height="12px"
                    width="20px"
                    bg="#ADCCDD"
                  />
                </HStack>
                <HStack spacing={2}>
                  <HelivanButton variant="ghost" onClick={handleBack}>
                    Back
                  </HelivanButton>
                  <HelivanButton
                    variant="action"
                    onClick={handleDownload}
                    isDisabled={
                      !selectedSubtopic ||
                      (reportType === 'dataset'
                        ? filteredPerspectives.length === 0
                        : selectedTwinIds.size === 0 ||
                          selectedTwinIds.size > 10)
                    }
                    isLoading={isLoading}
                  >
                    Download
                  </HelivanButton>
                </HStack>
              </Flex>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
